<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0 ballot-page">
         <BaseBreadcrumb
            :title="page.title"
            :icon="page.icon"
            :breadcrumbs="breadcrumbs"
         ></BaseBreadcrumb>

         <v-alert v-if="message" :type="messageType" dismissible @input="message = ''">
            <span v-html="message"></span>
         </v-alert>

         <v-card class="mb-4" outlined>
            <v-card-title class="primary white--text">
               <v-icon color="white" class="mr-2">mdi-ballot-outline</v-icon>
               Election Ballot
               <v-spacer></v-spacer>
               <v-btn text dark @click="backToElections">
                  <v-icon left>mdi-arrow-left</v-icon>
                  Back
               </v-btn>
            </v-card-title>
            <v-card-text class="pt-5">
               <v-row align="center">
                  <v-col cols="12" md="8">
                     <h2 class="mb-2">{{ electionTitle }}</h2>
                     <div class="body-2 grey--text text--darken-1">
                        Review candidate details carefully, enter your TAC, then submit your vote for each position.
                        Please check your email after submitting your vote.
                     </div>
                  </v-col>
                  <v-col cols="12" md="4">
                     <v-text-field
                        v-model="tac"
                        label="Voting TAC"
                        prepend-inner-icon="mdi-shield-key-outline"
                        outlined
                        dense
                        maxlength="10"
                        hint="Use the TAC sent to your email."
                        persistent-hint
                     ></v-text-field>
                  </v-col>
               </v-row>
            </v-card-text>
         </v-card>

         <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

         <template v-else>
            <v-alert v-if="positions.length === 0" type="info" outlined>
               No ballot positions or candidates have been configured for this election yet.
            </v-alert>

            <v-card
               v-for="position in positions"
               :key="position.id"
               class="mb-5 ballot-position"
               outlined
            >
               <v-card-title class="position-header">
                  <div>
                     <div class="headline">{{ position.title }}</div>
                     <div v-if="position.description" class="body-2 grey--text text--darken-1 mt-1">{{ position.description }}</div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-chip v-if="position.wasvoted" color="success" dark>Voted</v-chip>
                  <v-chip v-else color="primary" outlined>
                     {{ voteInstruction(position) }}
                  </v-chip>
               </v-card-title>

               <v-card-text>
                  <v-alert v-if="!position.candidates || position.candidates.length === 0" type="info" text>
                     No candidates are available for this position.
                  </v-alert>

                  <v-radio-group
                     v-if="isSingleChoice(position)"
                     v-model="selections[position.id]"
                     :disabled="position.wasvoted"
                  >
                     <v-row>
                        <v-col
                           v-for="candidate in position.candidates"
                           :key="candidate.id"
                           cols="12"
                           md="6"
                           xl="4"
                        >
                           <v-card
                              class="candidate-card"
                              :class="{ selected: String(selections[position.id]) === String(candidate.id) }"
                              outlined
                              @click="!position.wasvoted && setSingleSelection(position, candidate)"
                           >
                              <v-row no-gutters>
                                 <v-col cols="12" sm="4">
                                    <v-img
                                       v-if="candidatePhoto(candidate)"
                                       :src="candidatePhoto(candidate)"
                                       height="190"
                                       class="candidate-photo"
                                    ></v-img>
                                    <div v-else class="candidate-placeholder">
                                       <v-icon size="64" color="grey lighten-1">mdi-account-circle</v-icon>
                                    </div>
                                 </v-col>
                                 <v-col cols="12" sm="8">
                                    <v-card-text>
                                       <div class="d-flex align-start">
                                          <v-radio :value="candidate.id" class="mt-0 mr-2"></v-radio>
                                          <div>
                                             <h3 class="mb-1">{{ candidate.cndname }}</h3>
                                             <div class="caption grey--text text--darken-1 mb-2">
                                                {{ candidate.cndmemberid || 'No Member ID' }}
                                                <span v-if="candidate.membertype"> · {{ candidate.membertype }}</span>
                                             </div>
                                             <v-chip v-if="candidate.isuncontested" x-small color="orange" dark class="mr-1">Uncontested</v-chip>
                                             <v-chip v-if="candidate.isappointed" x-small color="purple" dark>Appointed</v-chip>
                                          </div>
                                       </div>
                                       <p v-if="candidate.description" class="mt-3 mb-1">{{ candidate.description }}</p>
                                       <p v-if="candidate.details" class="body-2 grey--text text--darken-2 mb-0">{{ candidate.details }}</p>
                                    </v-card-text>
                                 </v-col>
                              </v-row>
                           </v-card>
                        </v-col>
                     </v-row>
                  </v-radio-group>

                  <v-row v-else>
                     <v-col
                        v-for="candidate in position.candidates"
                        :key="candidate.id"
                        cols="12"
                        md="6"
                        xl="4"
                     >
                        <v-card
                           class="candidate-card"
                           :class="{ selected: isSelected(position, candidate.id) }"
                           outlined
                           @click="!position.wasvoted && toggleMultipleSelection(position, candidate)"
                        >
                           <v-row no-gutters>
                              <v-col cols="12" sm="4">
                                 <v-img
                                    v-if="candidatePhoto(candidate)"
                                    :src="candidatePhoto(candidate)"
                                    height="190"
                                    class="candidate-photo"
                                 ></v-img>
                                 <div v-else class="candidate-placeholder">
                                    <v-icon size="64" color="grey lighten-1">mdi-account-circle</v-icon>
                                 </div>
                              </v-col>
                              <v-col cols="12" sm="8">
                                 <v-card-text>
                                    <div class="d-flex align-start">
                                       <v-checkbox
                                          :input-value="isSelected(position, candidate.id)"
                                          class="mt-0 mr-2"
                                          readonly
                                       ></v-checkbox>
                                       <div>
                                          <h3 class="mb-1">{{ candidate.cndname }}</h3>
                                          <div class="caption grey--text text--darken-1 mb-2">
                                             {{ candidate.cndmemberid || 'No Member ID' }}
                                             <span v-if="candidate.membertype"> · {{ candidate.membertype }}</span>
                                          </div>
                                          <v-chip v-if="candidate.isuncontested" x-small color="orange" dark class="mr-1">Uncontested</v-chip>
                                          <v-chip v-if="candidate.isappointed" x-small color="purple" dark>Appointed</v-chip>
                                       </div>
                                    </div>
                                    <p v-if="candidate.description" class="mt-3 mb-1">{{ candidate.description }}</p>
                                    <p v-if="candidate.details" class="body-2 grey--text text--darken-2 mb-0">{{ candidate.details }}</p>
                                 </v-card-text>
                              </v-col>
                           </v-row>
                        </v-card>
                     </v-col>
                  </v-row>
               </v-card-text>

               <v-card-actions class="px-4 pb-4">
                  <v-spacer></v-spacer>
                  <v-btn
                     color="primary"
                     :loading="busyPositionId === position.id"
                     :disabled="position.wasvoted || !canSubmit(position)"
                     @click="submitPosition(position)"
                  >
                     <v-icon left>mdi-send-check</v-icon>
                     Submit Vote
                  </v-btn>
               </v-card-actions>
            </v-card>
         </template>
      </v-container>
   </dashboard-layout>
</template>

<script>
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
const axios = require('axios')

export default {
   name: 'MOMElectionBallot',
   components: { DashboardLayout, BaseBreadcrumb },
   props: ['user', 'electid'],
   data() {
      return {
         page: { title: 'Election Ballot', icon: 'mdi-ballot-outline' },
         breadcrumbs: [
            { text: 'Dashboard', disabled: false, to: '/dashboard' },
            { text: 'Elections', disabled: false, to: '/member/elections' },
            { text: 'Ballot', disabled: true },
         ],
         loading: true,
         positions: [],
         selections: {},
         tac: '',
         busyPositionId: null,
         message: '',
         messageType: 'info',
      }
   },
   computed: {
      currentUser() {
         return this.user || this.$page.user || {}
      },
      electionTitle() {
         if (!this.positions.length) return 'Election'
         return this.positions[0].election_title || 'Election'
      },
   },
   mounted() {
      this.loadBallot()
   },
   methods: {
      loadBallot() {
         this.loading = true
         axios.get('/member/agm/electcatcndds/' + this.electid)
            .then(({ data }) => {
               this.positions = data.status === 'OK' && Array.isArray(data.data) ? data.data : []
               this.positions.forEach(position => {
                  if (this.isSingleChoice(position)) {
                     this.$set(this.selections, position.id, null)
                  } else {
                     this.$set(this.selections, position.id, [])
                  }
               })
               if (data.status !== 'OK') this.showMessage(data.error || 'Unable to load ballot.', 'error')
            })
            .catch(() => this.showMessage('Unable to load ballot. Please try again.', 'error'))
            .finally(() => { this.loading = false })
      },
      isSingleChoice(position) {
         return String(position.votetype || 'S').toUpperCase() !== 'M'
      },
      maxVotes(position) {
         const max = parseInt(position.noofvotes || 1)
         return Number.isNaN(max) || max < 1 ? 1 : max
      },
      voteInstruction(position) {
         if (this.isSingleChoice(position)) return 'Choose 1 candidate'
         return 'Choose up to ' + this.maxVotes(position) + ' candidates'
      },
      setSingleSelection(position, candidate) {
         this.$set(this.selections, position.id, candidate.id)
      },
      isSelected(position, candidateId) {
         const selected = this.selections[position.id] || []
         return selected.map(String).indexOf(String(candidateId)) !== -1
      },
      toggleMultipleSelection(position, candidate) {
         const selected = Array.isArray(this.selections[position.id]) ? [...this.selections[position.id]] : []
         const existingIndex = selected.map(String).indexOf(String(candidate.id))
         if (existingIndex !== -1) {
            selected.splice(existingIndex, 1)
         } else {
            if (selected.length >= this.maxVotes(position)) {
               this.showMessage('You can only choose up to ' + this.maxVotes(position) + ' candidates for ' + position.title + '.', 'warning')
               return
            }
            selected.push(candidate.id)
         }
         this.$set(this.selections, position.id, selected)
      },
      selectedCandidateIds(position) {
         const selected = this.selections[position.id]
         if (this.isSingleChoice(position)) return selected ? [selected] : []
         return Array.isArray(selected) ? selected : []
      },
      canSubmit(position) {
         return this.tac.trim().length > 0 && this.selectedCandidateIds(position).length > 0
      },
      submitPosition(position) {
         const candidateIds = this.selectedCandidateIds(position)
         if (!candidateIds.length) {
            this.showMessage('Please select at least one candidate for ' + position.title + '.', 'error')
            return
         }
         if (!this.tac.trim()) {
            this.showMessage('Please enter your voting TAC.', 'error')
            return
         }
         if (!window.confirm('Submit your vote for ' + position.title + '? This cannot be changed after submission.')) return

         this.busyPositionId = position.id
         axios.post('/member/agm/election/votes', {
            electid: this.electid,
            positionid: position.id,
            userid: this.currentUser.id,
            tac: this.tac.trim(),
            candidates: JSON.stringify(candidateIds.map(id => ({ id }))),
         }).then(({ data }) => {
            if (data.status === 'OK') {
               this.showMessage((data.message || 'Vote submitted.') + ' Please check your email after submitting your vote.', 'success')
               this.loadBallot()
            } else {
               this.showMessage(data.error, 'error')
            }
         }).catch(error => {
            const response = error.response && error.response.data
            const message = response && (response.error || response.message)
               ? (response.error || response.message)
               : 'Unable to submit vote. Please try again.'
            this.showMessage(message, 'error')
         })
           .finally(() => { this.busyPositionId = null })
      },
      candidatePhoto(candidate) {
         const value = candidate.photo_url || candidate.cndphoto || ''
         if (!value) return ''
         if (/^(https?:)?\/\//.test(value) || value.charAt(0) === '/') return value
         return '/photos/election/' + this.electid + '/' + value
      },
      backToElections() {
         window.location.href = '/member/elections'
      },
      showMessage(message, type) {
         this.message = message || 'An unexpected error occurred.'
         this.messageType = type
         window.scrollTo({ top: 0, behavior: 'smooth' })
      },
   },
}
</script>

<style scoped>
.ballot-page {
   max-width: 1500px;
}

.position-header {
   background: #f8fafc;
   border-bottom: 1px solid #e5e7eb;
}

.candidate-card {
   cursor: pointer;
   height: 100%;
   transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
}

.candidate-card:hover {
   border-color: #2f80ed;
   box-shadow: 0 8px 24px rgba(15, 23, 42, .12);
   transform: translateY(-1px);
}

.candidate-card.selected {
   border: 2px solid #2f80ed;
   box-shadow: 0 10px 28px rgba(47, 128, 237, .18);
}

.candidate-photo {
   background: #f1f5f9;
}

.candidate-placeholder {
   align-items: center;
   background: #f1f5f9;
   display: flex;
   height: 190px;
   justify-content: center;
}
</style>
